/**
 * Cloud implementation of the shared AttendanceApi. Node-scoped: binds the
 * currently-selected node id from localStorage (NodeContext keeps it in sync),
 * mirroring the groups/models adapters. The tunnel double-envelope peeling
 * already lives in attendance.service.ts, so this is a thin binding layer.
 */
import type { AttendanceApi, CameraRef } from '@trinetra/shared';
import * as svc from '../services/attendance.service';
import { getNodeCameraNames } from '../services/api.service';
import { selectedNodeId as nodeId } from './node';

/** Node id or '' — the shared pages render an empty state when no node is picked. */
const nid = () => nodeId() || '';

function toArray(res: any): any[] {
  if (Array.isArray(res)) return res;
  return res?.data ?? res?.cameras ?? [];
}

export const cloudAttendanceApi: AttendanceApi = {
  getConfig: () => svc.getAttendanceConfig(nid()),
  updateConfig: (patch) => svc.updateAttendanceConfig(nid(), patch),

  today: (date) => svc.getAttendanceToday(nid(), date),
  timeline: (params) => svc.getAttendanceTimeline(nid(), params),

  async listEmployees(params) {
    return toArray(await svc.listEmployees(nid(), params));
  },
  getEmployee: (id) => svc.getEmployee(nid(), id),
  createEmployee: (data) => svc.createEmployee(nid(), data),
  updateEmployee: (id, data) => svc.updateEmployee(nid(), id, data),
  deleteEmployee: (id) => svc.deleteEmployee(nid(), id),
  employeeDay: (id, date) => svc.getEmployeeDay(nid(), id, date),
  employeeMonthly: (id, month) => svc.getEmployeeMonthly(nid(), id, month),
  employeeSightings: (id, params) => svc.getEmployeeSightings(nid(), id, params),

  monthlyReport: (month) => svc.getMonthlyReport(nid(), month),
  setPayrollAdjustments: (month, employeeId, patch) => svc.setPayrollAdjustments(nid(), month, employeeId, patch),
  setPayrollBulkAdjustments: (month, patch) => svc.setPayrollBulkAdjustments(nid(), month, patch),
  addPayrollHoliday: (date, name) => svc.addPayrollHoliday(nid(), date, name),
  removePayrollHoliday: (date) => svc.removePayrollHoliday(nid(), date),
  setPayrollWorkingDays: (month, workingDays) => svc.setPayrollWorkingDays(nid(), month, workingDays),
  setPayrollSettings: (settings) => svc.setPayrollSettings(nid(), settings),

  uploadEmployeePhotos: (id, files) => svc.uploadEmployeePhotos(nid(), id, files),
  removeEmployeePhoto: (employeeId, photoId) => svc.removeEmployeePhoto(nid(), employeeId, photoId),
  employeePhotoDataUrl: (photoId) => svc.getEmployeePhotoDataUrl(nid(), photoId),

  async listUnknownFaceEvents(params) {
    return toArray(await svc.listUnknownFaceEvents(nid(), params));
  },
  createEmployeeFromEvent: (data) => svc.createEmployeeFromEvent(nid(), data),
  linkEventToEmployee: (employeeId, eventId) => svc.linkEventToEmployee(nid(), employeeId, eventId),
  eventImageDataUrl: (eventId, type) => svc.getEventImageDataUrl(nid(), eventId, type),

  async listCameraConfigs() {
    return toArray(await svc.listAttendanceCameras(nid()));
  },
  upsertCameraConfig: (cameraId, data) => svc.upsertAttendanceCamera(nid(), cameraId, data),
  /**
   * Cameras for the timeline filter and id→name labelling. Uses the name-only
   * catalog (`cameras.list`), not the full camera list: attendance only needs
   * id + name, and an attendance-only role must not be handed cameras.view,
   * which also carries camera credentials and live video.
   */
  async listCameras() {
    const id = nid();
    if (!id) return [];
    return toArray(await getNodeCameraNames(id)) as CameraRef[];
  },
};
